import { ApiSaleOrderService } from 'services';
import {
	ICreateInvoicePayload,
	IInvoice,
	IInvoiceFilters,
	IInvoiceListParams,
	IInvoiceListResponse,
	IInvoiceReceipt,
	IInvoiceReceiptFilters,
	IIssueInvoicePayload,
	ILinkReceiptsPayload,
	IPassengerInvoiceSummary,
} from './types';

export const createInvoice = (payload: ICreateInvoicePayload): Promise<IInvoice> => {
	return ApiSaleOrderService.post<IInvoice>('/invoices', payload).then((res) => res.data);
};

type InvoiceQueryParamValue = string | number | boolean | Array<string | number | boolean>;

const normalizeInvoiceFilters = (filters?: IInvoiceFilters): Record<string, InvoiceQueryParamValue> => {
	if (!filters) return {};

	return {
		...(filters.saleOrderId ? { saleOrderId: filters.saleOrderId } : {}),
		...(filters.type?.length ? { type: filters.type } : {}),
		...(filters.status?.length ? { status: filters.status } : {}),
		...(filters.passengerId ? { passengerId: filters.passengerId } : {}),
		...(filters.passengerDocument ? { passengerDocument: filters.passengerDocument } : {}),
		...(filters.agencyId ? { agencyId: filters.agencyId } : {}),
		...(filters.issueDateFrom ? { issueDateFrom: filters.issueDateFrom } : {}),
		...(filters.issueDateTo ? { issueDateTo: filters.issueDateTo } : {}),
		...(filters.dueDateFrom ? { dueDateFrom: filters.dueDateFrom } : {}),
		...(filters.dueDateTo ? { dueDateTo: filters.dueDateTo } : {}),
		...(filters.hasPendingBalance !== undefined ? { hasPendingBalance: filters.hasPendingBalance } : {}),
		...(filters.isOverdue !== undefined ? { isOverdue: filters.isOverdue } : {}),
		...(filters.saleOrderFullyCharged !== undefined ? { saleOrderFullyCharged: filters.saleOrderFullyCharged } : {}),
		...(filters.search ? { search: filters.search } : {}),
	};
};

const invoiceParamsSerializer = (params: Record<string, unknown>): string => {
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === '') return;

		if (Array.isArray(value)) {
			value.forEach((item) => {
				if (item !== undefined && item !== null && item !== '') {
					searchParams.append(key, String(item));
				}
			});
			return;
		}

		searchParams.append(key, String(value));
	});

	return searchParams.toString();
};

export const getInvoices = (params: IInvoiceListParams = {}): Promise<IInvoiceListResponse> => {
	const { page, size, sort, ...filters } = params;

	return ApiSaleOrderService.get<IInvoiceListResponse>('/invoices', {
		params: {
			...(page ? { page } : {}),
			...(size ? { size } : {}),
			...(sort ? { sort } : {}),
			...normalizeInvoiceFilters(filters),
		},
		paramsSerializer: invoiceParamsSerializer,
	}).then((res) => res.data);
};

/** Todas las facturas de un pasajero/cliente. Mismo DTO y filtros que /invoices. */
export const getInvoicesByPassenger = (
	passengerId: string,
	params: IInvoiceListParams = {},
): Promise<IInvoiceListResponse> => {
	const { page, size, sort, ...filters } = params;

	return ApiSaleOrderService.get<IInvoiceListResponse>(`/passengers/invoices/${passengerId}`, {
		params: {
			...(page ? { page } : {}),
			...(size ? { size } : {}),
			...(sort ? { sort } : {}),
			...normalizeInvoiceFilters(filters),
		},
		paramsSerializer: invoiceParamsSerializer,
	}).then((res) => res.data);
};

/** Resumen de saldos del cliente calculado por la API (no pagina, escala a cualquier volumen). */
export const getPassengerInvoiceSummary = (passengerId: string): Promise<IPassengerInvoiceSummary> => {
	return ApiSaleOrderService.get<IPassengerInvoiceSummary>(`/passengers/invoices/${passengerId}/summary`).then(
		(res) => res.data,
	);
};

/**
 * `agencyId` no es un filtro: es la agencia con la que el interceptor elige la
 * API key. Sin él la request sale con la key de la sesión, que al entrar por un
 * link a una orden de otro país todavía no cambió — y la llamada falla hasta
 * que se recarga la página.
 */
export const getInvoicesBySaleOrder = (saleOrderId: string, options?: { agencyId?: number }): Promise<IInvoice[]> => {
	return ApiSaleOrderService.get<IInvoice[]>(`saleorders/${saleOrderId}/invoices`, {
		agencyId: options?.agencyId,
	}).then((res) => res.data);
};

export const issueInvoice = (id: string, payload: IIssueInvoicePayload): Promise<IInvoice> => {
	return ApiSaleOrderService.patch<IInvoice>(`/invoices/${id}/issue`, payload).then((res) => res.data);
};

export const deleteInvoice = (id: string): Promise<void> => {
	return ApiSaleOrderService.delete(`/invoices/${id}`).then(() => undefined);
};

export const linkReceipts = (id: string, receiptIds: string[]): Promise<IInvoice> => {
	const payload: ILinkReceiptsPayload = { receiptIds };
	return ApiSaleOrderService.patch<IInvoice>(`/invoices/${id}/link-receipts`, payload).then((res) => res.data);
};

export const unlinkReceipt = (id: string, receiptId: string): Promise<IInvoice> => {
	return ApiSaleOrderService.delete<IInvoice>(`/invoices/${id}/receipts/${receiptId}`).then((res) => res.data);
};

export const getInvoiceReceipts = (
	filters: IInvoiceReceiptFilters,
	options?: { agencyId?: number },
): Promise<IInvoiceReceipt[]> => {
	return ApiSaleOrderService.get<IInvoiceReceipt[]>('/invoices/receipts', {
		params: filters,
		agencyId: options?.agencyId,
	}).then((res) => res.data);
};
